import { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, Loader2, Mail, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { postContact, postNewsletter } from '@/lib/api';

const ContactSection = ({ lang, t }) => {
  const [form, setForm] = useState({ name: '', email: '', company: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [nlSubmitting, setNlSubmitting] = useState(false);
  const [nlSubmitted, setNlSubmitted] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submitContact = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) return;
    setSubmitting(true);
    try {
      await postContact({
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim() || null,
        message: form.message.trim(),
        language: lang,
      });
      toast.success(t.contact.success);
      setSubmitted(true);
      setForm({ name: '', email: '', company: '', message: '' });
    } catch (err) {
      toast.error(t.contact.error);
    } finally {
      setSubmitting(false);
    }
  };

  const submitNewsletter = async (e) => {
    e.preventDefault();
    if (!newsletterEmail.trim()) return;
    setNlSubmitting(true);
    try {
      const res = await postNewsletter({ email: newsletterEmail.trim(), language: lang });
      if (res.already_subscribed) toast(t.contact.newsletter_already);
      else toast.success(t.contact.newsletter_success);
      setNlSubmitted(true);
      setNewsletterEmail('');
    } catch (err) {
      toast.error(t.contact.newsletter_error);
    } finally {
      setNlSubmitting(false);
    }
  };

  return (
    <section id="contact" className="relative py-20 sm:py-24 lg:py-28 bg-[rgb(var(--bg-1))]" data-testid="contact-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mb-12">
          <div className="kicker-mono mb-3">{t.contact.kicker}</div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-[650] tracking-tight text-white" data-testid="contact-title">{t.contact.title}</h2>
          <p className="mt-4 text-white/70 text-base sm:text-lg">{t.contact.subtitle}</p>
        </div>

        <div className="grid lg:grid-cols-5 gap-6">
          {/* Contact Form */}
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            onSubmit={submitContact}
            className="lg:col-span-3 rounded-2xl border border-white/10 bg-[rgb(var(--bg-2))] p-6 sm:p-8"
            data-testid="contact-form"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="name" className="kicker-mono block mb-2 text-white/60">{t.contact.labels.name}</label>
                <input id="name" name="name" type="text" required value={form.name} onChange={onChange} placeholder={t.contact.placeholders.name} className="field" data-testid="contact-input-name" />
              </div>
              <div>
                <label htmlFor="email" className="kicker-mono block mb-2 text-white/60">{t.contact.labels.email}</label>
                <input id="email" name="email" type="email" required value={form.email} onChange={onChange} placeholder={t.contact.placeholders.email} className="field" data-testid="contact-input-email" />
              </div>
            </div>
            <div className="mt-4">
              <label htmlFor="company" className="kicker-mono block mb-2 text-white/60">{t.contact.labels.company}</label>
              <input id="company" name="company" type="text" value={form.company} onChange={onChange} placeholder={t.contact.placeholders.company} className="field" data-testid="contact-input-company" />
            </div>
            <div className="mt-4">
              <label htmlFor="message" className="kicker-mono block mb-2 text-white/60">{t.contact.labels.message}</label>
              <textarea id="message" name="message" rows={5} required value={form.message} onChange={onChange} placeholder={t.contact.placeholders.message} className="field resize-none" data-testid="contact-input-message" />
            </div>
            <div className="mt-6 flex items-center justify-between gap-4 flex-wrap">
              {submitted && (
                <div className="flex items-center gap-2 text-sm text-emerald-400" data-testid="contact-success">
                  <CheckCircle2 size={16} /> {t.contact.success}
                </div>
              )}
              <button type="submit" className="btn-primary ml-auto" disabled={submitting} data-testid="contact-form-submit-button">
                {submitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={15} />}
                {t.contact.submit}
              </button>
            </div>
          </motion.form>

          {/* Newsletter */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="lg:col-span-2"
          >
            <div className="gradient-border h-full">
              <div className="inner h-full p-6 sm:p-8 flex flex-col">
                <div className="h-11 w-11 rounded-xl bg-[rgba(5,126,243,.12)] border border-[rgba(5,126,243,.35)] flex items-center justify-center text-[rgb(var(--brand-electric))]">
                  <Mail size={20} />
                </div>
                <h3 className="mt-5 font-display text-2xl font-[650] text-white">{t.contact.newsletter_title}</h3>
                <p className="mt-2 text-sm text-white/65 leading-relaxed">{t.contact.newsletter_subtitle}</p>
                <form onSubmit={submitNewsletter} className="mt-6 flex-1 flex flex-col gap-3" data-testid="newsletter-form">
                  <input
                    type="email"
                    value={newsletterEmail}
                    onChange={(e) => setNewsletterEmail(e.target.value)}
                    placeholder={t.contact.newsletter_placeholder}
                    className="field"
                    required
                    data-testid="newsletter-input"
                  />
                  <button type="submit" className="btn-primary" disabled={nlSubmitting} data-testid="newsletter-submit-button">
                    {nlSubmitting ? <Loader2 size={15} className="animate-spin" /> : <Send size={14} />}
                    {t.contact.newsletter_cta}
                  </button>
                  {nlSubmitted && (
                    <div className="text-xs text-emerald-400 flex items-center gap-1.5" data-testid="newsletter-success">
                      <CheckCircle2 size={14} /> {t.contact.newsletter_success}
                    </div>
                  )}
                </form>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
